/**
 * /mp3-to-video (SEO_BRIEF_12 section 4): an audio file and an optional picture made into an MP4,
 * in this tab, on the /burn-subtitles engine and the browsers it runs in.
 *
 * The page (tools/still_video.html) loads this bundle beside boot.js. It owns the drop zone, the
 * picture, the shape, the title, the preview, the progress line, the result and the Save button,
 * and starts the worker (`still-video-worker.ts`, where mediabunny lives) on the first audio file,
 * so a visitor who never picks one never downloads it. One worker per file: a new file
 * terminates the old one.
 *
 * THE PICTURE is drawn here, on the page's own canvas at the video's size (lib/still-frame.ts),
 * where the site's font is loaded: the visitor's picture whole on Obsidian, or, without one, the
 * title in Paper White. A photo is decoded no wider than the wide frame (`createImageBitmap`'s
 * resize, upright by its EXIF orientation), so a 48-megapixel photo never sits in memory at full
 * size. "Make the video" hands the canvas to the worker as an ImageBitmap. The file's own cover
 * art is never used unasked. Pictures dropped on the audio drop zone are taken as the picture.
 *
 * Save hands the MP4 over from a tap, which is what Safari on an iPhone needs to download it.
 * Every word is in the template (`data-still-label`, the error paragraphs); this file writes only
 * numbers, sizes and file names.
 *
 * THE USAGE COUNTER (base/src/lib/usage.ts), under the root's `data-usage-tool` slug: "loaded"
 * once per audio file that opened and can be made into a video, "failed" once per file that
 * could not be (a video, a file that isn't audio, a sound this browser can't use), "downloaded"
 * for each Save, as `mp4`. Pictures are not counted. An audio file opened and never saved is the
 * counter's own `no_result` when the page is left.
 */

import { classify, extensionOf, mountDropzone } from '../../base/src/lib/dropzone.ts';
import { clock, fileSize } from '../../base/src/lib/format.ts';
import { mediaFormat, usageCounter } from '../../base/src/lib/usage.ts';
import { mp3WasmUrl } from '../../../../src/media/mp3-assets.ts';
import {
  DEFAULT_SHAPE,
  defaultTitle,
  isShape,
  SHAPES,
  type StillShape,
  stillName,
} from '../../../../src/media/still-plan.ts';
import { canBurnHere } from './lib/codec.ts';
import { drawStill, type StillContext, titleFont } from './lib/still-frame.ts';
import type {
  FromStillWorker,
  StillFailure,
  StillProbe,
  ToStillWorker,
} from './lib/still-protocol.ts';

type ErrorKey = Exclude<StillFailure, 'cancelled'> | 'video-file' | 'picture';

/** Pictures by type, for a file dropped on the audio drop zone (`classify` has no image kind). */
const PICTURE_EXT: ReadonlySet<string> = new Set([
  'jpg',
  'jpeg',
  'png',
  'webp',
  'gif',
  'avif',
  'bmp',
  'heic',
  'heif',
]);

/** The widest frame: a picture is decoded no wider, whichever shape is chosen. */
const PICTURE_WIDTH = Math.max(...Object.values(SHAPES).map((s) => s.width));

function isPicture(file: File): boolean {
  return file.type.startsWith('image/') || PICTURE_EXT.has(extensionOf(file.name));
}

function buildId(): string | null {
  return document.querySelector<HTMLMetaElement>('meta[name="build-id"]')?.content.trim() || null;
}

function workerUrl(): string {
  const build = buildId();
  return build
    ? `/static/build/burn/still-video-worker.js?v=${encodeURIComponent(build)}`
    : '/static/build/burn/still-video-worker.js';
}

function q<T extends Element = HTMLElement>(root: ParentNode, sel: string): T | null {
  return root.querySelector<T>(sel);
}

function saveBlob(blob: Blob, name: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.rel = 'noopener';
  a.style.display = 'none';
  document.body.append(a);
  a.click();
  a.remove();
  // Revoking in the same task cancels the download in some engines.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * The picture upright, no wider than the widest frame. A browser that ignores the resize hands
 * the photo back whole, and it is drawn down onto the canvas and closed all the same.
 */
async function decodePicture(file: File): Promise<ImageBitmap> {
  try {
    return await createImageBitmap(file, {
      imageOrientation: 'from-image',
      resizeWidth: PICTURE_WIDTH,
      resizeQuality: 'high',
    });
  } catch (err) {
    // Not a picture this browser opens (a HEIC photo in Chrome) fails here too, and below.
    if (!(err instanceof TypeError)) throw err;
    return createImageBitmap(file);
  }
}

function mount(root: HTMLElement): void {
  const unsupported = q(document, '[data-still-unsupported]');
  if (!canBurnHere(globalThis as unknown as Record<string, unknown>)) {
    if (unsupported) unsupported.hidden = false;
    root.hidden = true;
    return;
  }

  const host = q(root, '[data-dropzone-host]');
  const fileLine = q(root, '[data-still-file]');
  const options = q(root, '[data-still-options]');
  const pictureInput = q<HTMLInputElement>(root, '[data-still-picture-input]');
  const addPicture = q<HTMLButtonElement>(root, '[data-still-picture-add]');
  const removePicture = q<HTMLButtonElement>(root, '[data-still-picture-remove]');
  const titleField = q(root, '[data-still-title-field]');
  const titleInput = q<HTMLInputElement>(root, '[data-still-title]');
  const canvas = q<HTMLCanvasElement>(root, '[data-still-canvas]');
  const start = q<HTMLButtonElement>(root, '[data-still-start]');
  const working = q(root, '[data-still-working]');
  const bar = q(root, '[data-still-bar]');
  const fill = q(root, '[data-still-fill]');
  const pctSlot = q(root, '[data-still-working] [data-slot="pct"]');
  const cancel = q<HTMLButtonElement>(root, '[data-still-cancel]');
  const result = q(root, '[data-still-result]');
  const save = q<HTMLButtonElement>(root, '[data-still-save]');
  const status = q(root, '[data-still-status]');
  if (!host || !save || !canvas) return;

  const usageTool = root.dataset['usageTool'] ?? '';
  const usage = usageTool ? usageCounter(usageTool) : null;
  const audioFormat = (file: File) => mediaFormat(extensionOf(file.name), 'audio');

  const label = (key: string): string =>
    q(root, `[data-still-label="${key}"]`)?.textContent?.trim() ?? '';
  const fillSlots = (scope: ParentNode, name: string, text: string) => {
    for (const el of scope.querySelectorAll<HTMLElement>(`[data-slot="${name}"]`)) {
      el.textContent = text;
    }
  };
  const shapeRadios = () =>
    Array.from(root.querySelectorAll<HTMLInputElement>('[data-still-shape]'));

  let worker: Worker | null = null;
  let audio: File | null = null;
  let probe: StillProbe | null = null;
  let picture: ImageBitmap | null = null;
  let busy = false;
  let done: { blob: Blob; name: string } | null = null;

  /* ── what the page shows ─────────────────────────────────────────────── */

  const showError = (key: ErrorKey | null) => {
    for (const el of root.querySelectorAll<HTMLElement>('[data-still-error]')) {
      el.hidden = el.dataset['stillError'] !== key;
    }
  };
  /** Take one sentence away and leave any other standing (a picture fixes only its own). */
  const hideError = (key: ErrorKey) => {
    const el = q(root, `[data-still-error="${key}"]`);
    if (el) el.hidden = true;
  };
  const setStatus = (text: string) => {
    if (status) status.textContent = text;
  };
  const setProgress = (fraction: number | null) => {
    if (bar) bar.dataset['indeterminate'] = fraction === null ? 'true' : 'false';
    const pct = fraction === null ? 0 : Math.max(0, Math.min(100, Math.floor(fraction * 100)));
    fill?.style.setProperty('--progress-pct', `${pct}%`);
    if (pctSlot) pctSlot.textContent = String(pct);
  };
  const refreshStart = () => {
    if (start) start.disabled = busy || !probe;
  };
  const setWorking = (on: boolean) => {
    busy = on;
    if (working) working.hidden = !on;
    if (on) setProgress(null);
    // The choices hold still while the video is made: the result is the one they asked for.
    for (const radio of shapeRadios()) {
      radio.disabled = on || (probe !== null && !probe.shapes[radio.value as StillShape]);
    }
    if (titleInput) titleInput.disabled = on;
    if (pictureInput) pictureInput.disabled = on;
    if (addPicture) addPicture.disabled = on;
    if (removePicture) removePicture.disabled = on;
    refreshStart();
  };
  const clearResult = () => {
    done = null;
    save.hidden = true;
    save.disabled = true;
    if (result) result.hidden = true;
  };

  /* ── the picture ─────────────────────────────────────────────────────── */

  const shape = (): StillShape => {
    const value = shapeRadios().find((r) => r.checked)?.value;
    return isShape(value) ? value : DEFAULT_SHAPE;
  };

  /** Draw the frame the video will show, at its size. */
  const redraw = () => {
    const size = SHAPES[shape()];
    canvas.width = size.width;
    canvas.height = size.height;
    const ctx = canvas.getContext('2d') as unknown as StillContext | null;
    if (!ctx) return;
    drawStill(ctx, size, {
      picture: picture ? { source: picture, width: picture.width, height: picture.height } : null,
      title: titleInput?.value ?? '',
    });
  };

  // The title is drawn in the site's font: drawn again once it has loaded, if it wasn't yet.
  const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
  fonts
    ?.load(titleFont(64))
    .then(() => redraw())
    .catch(() => {});

  const showPicture = (on: boolean) => {
    if (removePicture) removePicture.hidden = !on;
    if (titleField) titleField.hidden = on;
  };

  const setPicture = async (file: File) => {
    // The choices hold still while the video is made, a picture dropped on the zone included.
    if (busy) return;
    let bitmap: ImageBitmap;
    try {
      bitmap = await decodePicture(file);
    } catch (err) {
      console.warn('[mp3-to-video] picture', String(err));
      showError('picture');
      return;
    }
    // Started while the picture opened: the video is made with the picture it started with.
    if (busy) {
      bitmap.close();
      return;
    }
    picture?.close();
    picture = bitmap;
    hideError('picture');
    showPicture(true);
    clearResult();
    redraw();
  };

  const dropPicture = () => {
    picture?.close();
    picture = null;
    if (pictureInput) pictureInput.value = '';
    showPicture(false);
    clearResult();
    redraw();
  };

  /* ── the worker ──────────────────────────────────────────────────────── */

  const stopWorker = () => {
    worker?.terminate();
    worker = null;
  };

  const failed = (file: File, reason: ErrorKey, detail?: string) => {
    if (detail) console.warn('[mp3-to-video]', reason, detail);
    setWorking(false);
    usage?.failed(file, audioFormat(file));
    showError(reason);
    setStatus('');
  };

  /** The shapes this browser can make, as the radios show them; false when there are none. */
  const offerShapes = (opened: StillProbe): boolean => {
    const radios = shapeRadios();
    for (const radio of radios) radio.disabled = !opened.shapes[radio.value as StillShape];
    const usable = radios.filter((r) => !r.disabled);
    if (usable.length === 0) return false;
    if (!usable.some((r) => r.checked)) {
      for (const radio of radios) radio.checked = radio === usable[0];
    }
    return true;
  };

  const onProbed = (file: File, opened: StillProbe) => {
    probe = opened;
    if (fileLine) {
      fileLine.hidden = false;
      fillSlots(
        fileLine,
        'file',
        `${file.name} · ${fileSize(file.size)} · ${clock(opened.durationS)}`,
      );
    }
    if (opened.route === 'refuse') {
      probe = null;
      failed(file, 'cannot-decode', `cannot play ${String(opened.codec)}`);
      return;
    }
    if (!offerShapes(opened)) {
      probe = null;
      failed(file, 'cannot-encode', 'no shape can be encoded here');
      return;
    }
    // Counted once the audio can be made into a video here; a refusal above counts as failed.
    usage?.loaded(file, audioFormat(file));
    if (titleInput) titleInput.value = defaultTitle(file.name);
    if (options) options.hidden = false;
    redraw();
    refreshStart();
  };

  const onDone = (file: File, msg: Extract<FromStillWorker, { type: 'done' }>) => {
    setWorking(false);
    const blob = new Blob([msg.buffer], { type: 'video/mp4' });
    const name = stillName(file.name);
    done = { blob, name };
    fillSlots(root, 'after', `${name} · ${fileSize(blob.size)}`);
    if (result) result.hidden = false;
    save.hidden = false;
    save.disabled = false;
    setStatus(label('ready'));
  };

  const spawn = (file: File): Worker => {
    stopWorker();
    const w = new Worker(workerUrl(), { type: 'module' });
    worker = w;
    w.onmessage = (ev: MessageEvent<FromStillWorker>) => {
      if (worker !== w) return;
      const msg = ev.data;
      if (msg.type === 'probed') {
        onProbed(file, msg.probe);
      } else if (msg.type === 'progress') {
        setProgress(msg.fraction);
      } else if (msg.type === 'done') {
        onDone(file, msg);
      } else if (msg.type === 'failed') {
        if (msg.reason === 'cancelled') {
          setWorking(false);
          setStatus(label('cancelled'));
          return;
        }
        failed(file, msg.reason, msg.detail);
      }
    };
    w.onerror = (event) => {
      // A worker that failed to load (offline, blocked) answers like an unreadable file rather
      // than leaving the page on a progress bar.
      event.preventDefault();
      if (worker !== w) return;
      stopWorker();
      failed(file, busy ? 'failed' : 'unreadable');
    };
    return w;
  };

  const open = (file: File) => {
    clearResult();
    showError(null);
    setStatus('');
    audio = file;
    probe = null;
    setWorking(false);
    if (options) options.hidden = true;
    if (fileLine) fileLine.hidden = true;
    spawn(file).postMessage({ type: 'probe', file } satisfies ToStillWorker);
  };

  /* ── input ───────────────────────────────────────────────────────────── */

  mountDropzone(host, {
    onFiles: (dropped) => {
      // A picture dropped here is the picture; the first other file is the audio.
      const pic = dropped.find(isPicture);
      const file = dropped.find((f) => !isPicture(f));
      if (pic) void setPicture(pic);
      if (!file) return;
      const kind = classify(file);
      if (kind === 'audio') {
        open(file);
        return;
      }
      stopWorker();
      clearResult();
      probe = null;
      setWorking(false);
      setStatus('');
      if (options) options.hidden = true;
      if (fileLine) fileLine.hidden = true;
      if (kind === 'container' || kind === 'other-video') {
        usage?.failed(file, mediaFormat(extensionOf(file.name), 'video'));
        showError('video-file');
      } else {
        usage?.failed(file, kind === 'archive' ? 'zip' : 'unknown');
        showError('unreadable');
      }
    },
  });

  // The button opens the picture's own file input: one tap, as a phone's photo picker needs.
  addPicture?.addEventListener('click', () => pictureInput?.click());
  pictureInput?.addEventListener('change', () => {
    const file = pictureInput.files?.[0];
    if (file) void setPicture(file);
  });
  removePicture?.addEventListener('click', dropPicture);
  for (const radio of shapeRadios()) {
    radio.addEventListener('change', () => {
      clearResult();
      redraw();
    });
  }
  titleInput?.addEventListener('input', () => {
    clearResult();
    redraw();
  });

  start?.addEventListener('click', () => {
    const file = audio;
    const opened = probe;
    if (!file || !opened || busy) return;
    redraw();
    clearResult();
    showError(null);
    setStatus('');
    setWorking(true);
    createImageBitmap(canvas).then(
      (frame) => {
        if (!busy) {
          frame.close();
          return;
        }
        // A stop terminates the worker: a new one makes the video, with nothing to open first.
        const w = worker ?? spawn(file);
        const message: ToStillWorker = {
          type: 'make',
          file,
          frame,
          shape: shape(),
          route: opened.route,
          wasmUrl: mp3WasmUrl(buildId()),
        };
        w.postMessage(message, [frame]);
      },
      (err) => failed(file, 'failed', String(err)),
    );
  });

  // Stopping ends the worker outright, whatever it was doing: "Make the video" starts a new one.
  cancel?.addEventListener('click', () => {
    if (!busy) return;
    stopWorker();
    setWorking(false);
    setStatus(label('cancelled'));
  });

  save.addEventListener('click', () => {
    if (!done) return;
    saveBlob(done.blob, done.name);
    usage?.downloaded('mp4');
    setStatus(label('saved'));
  });

  showPicture(false);
  redraw();
}

const root = document.querySelector<HTMLElement>('[data-still-video]');
if (root) mount(root);
